import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import type { DriverRegistration } from "../../types/driver-registration.type";
import type { DriverRegistrationShare } from "../../types/driver-registration-share.type";
import { getDriverRegistrations } from "../../api/driver-registrations.api";
import { usePassword } from "../../hooks/use-password.hook";
import "./driver-registration.view.css";
import { getOrganizations } from "../../api/organization.api";
import type { Organization } from "../../types/organization.type";
import { ShareModalComponent } from "./components/share-modal.component";
import { DriverRegistrationSharesTableComponent } from "./components/driver-registration-shares-table.component";

type DriverRegistrationColumnKey = Exclude<
  keyof DriverRegistration,
  "shares"
>;

const COLUMNS: { key: DriverRegistrationColumnKey; label: string }[] = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "zipCode", label: "Zipcode" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "usCitizen", label: "US Citizen" },
  { key: "vehicleType", label: "Vehicle Type" },
  { key: "vehicleOwnType", label: "Vehicle Own Type" },
  { key: "validLicense", label: "Valid License" },
  { key: "haveCDL", label: "Have CDL" },
  { key: "activeMCNumber", label: "Active MC Number" },
  { key: "driverType", label: "Driver Type" },
  { key: "overnightDelivery", label: "Overnight Delivery" },
  { key: "canadaLoads", label: "Canada Loads" },
  { key: "yearsOfExperience", label: "Years Of Experience" },
  { key: "acknowledgementVersion", label: "Acknowledgement Version" },
];

function formatCell(value: DriverRegistration[DriverRegistrationColumnKey]) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return value;
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string } | undefined)?.message ??
      error.message
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}

export function DriverRegistrationView() {
  const password = usePassword();
  const [driverRegistrations, setDriverRegistrations] = useState<
    DriverRegistration[]
  >([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [sharingRegistrationId, setSharingRegistrationId] =
    useState<string>("");

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    setError("");
    getDriverRegistrations(password, { signal: controller.signal })
      .then((data) => {
        setDriverRegistrations(data.driverRegistrations);
      })
      .catch((error) => {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }
        setError(getErrorMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    getOrganizations(password, { signal: controller.signal })
      .then((data) => {
        setOrganizations(data.organizations);
      })
      .catch((error) => {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }
        setError(getErrorMessage(error));
      });

    return () => {
      controller.abort();
    };
  }, [password]);

  const sharingRegistration = driverRegistrations.find(
    (registration) =>
      registration.driverRegistrationId === sharingRegistrationId
  );

  function handleShared(share: DriverRegistrationShare) {
    setDriverRegistrations((registrations) =>
      registrations.map((registration) => {
        if (registration.driverRegistrationId !== sharingRegistrationId) {
          return registration;
        }
        const existingShares = registration.shares ?? [];
        const otherShares = existingShares.filter(
          (existingShare) =>
            existingShare.organizationId !== share.organizationId
        );
        return {
          ...registration,
          shares: [...otherShares, share],
        };
      })
    );
  }

  return (
    <div className="driver-registration-view">
      {isLoading ? (
        <div className="registration-loading" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <span>Loading driver registrations…</span>
        </div>
      ) : error ? (
        <p className="registration-error" role="alert">
          Failed to load driver registrations: {error}
        </p>
      ) : !driverRegistrations || driverRegistrations.length === 0 ? (
        <p className="registration-table-empty">
          No driver registrations found.
        </p>
      ) : (
        <div className="registration-table-wrapper">
          <table className="registration-table">
            <thead>
              <tr>
                <th></th>
                {COLUMNS.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {driverRegistrations.map((registration, index) => (
                <Fragment key={`${registration.email}-${index}`}>
                  <tr>
                    <td>
                      <button
                        className="share-button"
                        onClick={() =>
                          setSharingRegistrationId(
                            registration.driverRegistrationId
                          )
                        }
                      >
                        Share
                      </button>
                    </td>
                    {COLUMNS.map((column) => (
                      <td key={column.key}>
                        {formatCell(registration[column.key])}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td
                      className="shares-table-cell"
                      colSpan={COLUMNS.length + 1}
                    >
                      <DriverRegistrationSharesTableComponent
                        shares={registration.shares ?? []}
                        organizations={organizations}
                      />
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sharingRegistration && (
        <ShareModalComponent
          isOpen
          onClose={() => setSharingRegistrationId("")}
          driverRegistration={sharingRegistration}
          organizations={organizations}
          onShared={handleShared}
        />
      )}
    </div>
  );
}
